import { useEffect, useRef, useState } from 'react';

const facts = [
  { icon: 'ri-building-line', end: 500, suffix: '+', label: 'Projects Completed', sublabel: 'Flat roofs installed & repaired' },
  { icon: 'ri-award-line', end: 5, suffix: '', label: 'Certifications', sublabel: 'Major manufacturers' },
  { icon: 'ri-map-pin-2-line', end: 67, suffix: '+', label: 'Counties Served', sublabel: 'Across NJ & PA' },
  { icon: 'ri-group-line', end: 98, suffix: '%', label: 'Satisfaction Rate', sublabel: 'From verified clients' },
];

function CountUp({ end, suffix, active }: { end: number; suffix: string; active: boolean }) {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!active) return;
    let startTime: number | null = null;
    const duration = 1800;
    const step = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(eased * end));
      if (progress < 1) requestAnimationFrame(step);
      else setCount(end);
    };
    requestAnimationFrame(step);
  }, [active, end]);

  return <span>{count}{suffix}</span>;
}

export default function AwesomeFactsPanel() {
  const ref = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setActive(true); observer.disconnect(); } },
      { threshold: 0.2 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={ref} className="w-full h-full bg-gray-950 text-white flex flex-col">

      {/* Header */}
      <div className="px-8 pt-10 pb-6 border-b border-white/10">
        <p className="text-xs tracking-[0.25em] text-orange-400 uppercase mb-1">By the numbers</p>
        <h2 className="text-2xl font-display font-light tracking-tight">
          Awesome <span className="font-bold">Facts</span>
        </h2>
      </div>

      {/* 2x2 stats grid */}
      <div className="grid grid-cols-2 flex-1">
        {facts.map((fact, i) => (
          <div
            key={fact.label}
            className={`flex flex-col justify-center gap-2 px-7 py-7 transition-colors hover:bg-white/5 cursor-default
              ${i % 2 === 0 ? 'border-r border-white/10' : ''}
              ${i < 2 ? 'border-b border-white/10' : ''}`}
          >
            <div className="w-8 h-8 flex items-center justify-center">
              <i className={`${fact.icon} text-xl text-orange-400`} />
            </div>
            <p className="text-4xl font-display font-light tabular-nums text-white leading-none">
              <CountUp end={fact.end} suffix={fact.suffix} active={active} />
            </p>
            <div>
              <p className="text-xs font-semibold text-white leading-snug">{fact.label}</p>
              <p className="text-xs text-gray-500 mt-0.5">{fact.sublabel}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Orange footer — 20+ years */}
      <div className="bg-orange-600 px-8 py-6 flex items-center gap-5">
        <div className="w-10 h-10 flex items-center justify-center border-2 border-white/40 rounded-full flex-shrink-0">
          <i className="ri-calendar-check-line text-lg text-white" />
        </div>
        <div>
          <p className="text-3xl font-display font-black leading-none tabular-nums">
            <CountUp end={20} suffix="+" active={active} />
          </p>
          <p className="text-xs font-semibold tracking-[0.2em] uppercase text-white/80 mt-1">
            Years of Experience
          </p>
        </div>
        <p className="text-xs text-white/60 leading-relaxed ml-auto max-w-[160px] text-right hidden xl:block">
          Serving NJ &amp; PA with proven commercial roofing expertise
        </p>
      </div>

    </div>
  );
}
